import type { SearchApiFacetStats, SearchApiFacetValueDistribution } from '~/types/searchApi'

type RawHit = { id: string } & Record<string, unknown>

type RawSearchResult = {
    hits?: RawHit[]
    // The live API never omits these keys, but on a genuinely empty result set it
    // serialises them as a bare [] (PHP's empty-associative-array-as-JSON quirk) instead
    // of {} — see normalizeFacetContainer. Per-facet, a *numeric* facet's distribution
    // (only price.net today) is itself a plain number[] histogram (bucket counts, no
    // keys) rather than a value → count map — that's genuine Meilisearch behaviour for
    // numeric facets, not another instance of the empty-array quirk, and this adapter
    // passes it through untouched (see meiliResponseAdapter.spec.ts).
    facetDistribution?: Record<string, SearchApiFacetValueDistribution> | unknown[]
    facetStats?: Record<string, SearchApiFacetStats> | unknown[]
} & Record<string, unknown>

type RawMultiSearchResponse = {
    results?: RawSearchResult[]
} & Record<string, unknown>

/**
 * Normalises a raw A4G Search API `multi-search` response before it reaches
 * `@meilisearch/instant-meilisearch`:
 * - maps each hit's `id` to the `objectID` field instantsearch.js/vue-instantsearch
 *   require, independent of the adapter's own (optional, auto-detected) `primaryKey`
 *   handling — this seam is the one place that mapping is guaranteed to happen.
 * - normalises `facetDistribution`/`facetStats` to `{}` for a genuinely empty result
 *   set. Verified against a live capture: the API does not omit these keys — it
 *   returns them as a bare `[]`, not `{}` (PHP serialises an empty associative array as
 *   a JSON array), so downstream code would otherwise receive an array where an object
 *   is documented/expected.
 *
 * Deliberately does not touch pagination fields — `estimatedTotalHits` passes through
 * unchanged. The adapter's own `nbHits` already equals it in the default (non-finite)
 * pagination mode; see `deriveSearchPageCount` for the page-count the UI actually needs.
 */
export function adaptSearchResponseBody(raw: RawMultiSearchResponse): RawMultiSearchResponse {
    if (!Array.isArray(raw?.results)) {
        return raw
    }

    return {
        ...raw,
        results: raw.results.map(adaptSearchResult),
    }
}

function adaptSearchResult(result: RawSearchResult): RawSearchResult {
    return {
        ...result,
        hits: (result.hits ?? []).map(hit => ({ ...hit, objectID: hit.id })),
        facetDistribution: normalizeFacetContainer(result.facetDistribution),
        facetStats: normalizeFacetContainer(result.facetStats),
    }
}

function normalizeFacetContainer<T>(value: Record<string, T> | unknown[] | undefined): Record<string, T> {
    if (value == null || Array.isArray(value)) {
        return {}
    }

    return value
}

/**
 * Derives the numbered pager's page count directly from `estimatedTotalHits` (exposed
 * to the Vue layer as `nbHits`, which already equals it in the adapter's default
 * pagination mode).
 *
 * This is deliberately NOT the adapter's own `nbPages` value: in non-finite pagination
 * mode that value is a "does another page likely exist?" heuristic (current page + 1,
 * or +2 if the page came back full), not a real page count — see ticket #87's
 * pagination design decision. `estimatedTotalHits` is capped at the backend's
 * `maxTotalHits` (1000 on staging), so this naturally bounds the pager to the
 * reachable window (~42 pages at 24/page) without any extra clamping.
 */
export function deriveSearchPageCount(nbHits: number, hitsPerPage: number): number {
    if (hitsPerPage <= 0 || nbHits <= 0) {
        return 0
    }

    return Math.ceil(nbHits / hitsPerPage)
}
